import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { useActiveRole } from '@/features/auth/hooks/use-active-role';
import { useDataCache, makePaginatedCacheKey } from '@/store/data-cache-context';
import { useOrg } from '@/store/org-context';
import { useDebounce } from '@/lib/utils';
import { getUsers, updateUser, deleteUser, resetUserPassword } from '../api/users-api';
import { addOrgMember, getOrgInvites, revokeInvite, removeOrgMember } from '@/features/organizations/api/organizations-api';
import { UserRole } from '@/types/api';
import type { User, Invite, UpdateUserRequest, ResetPasswordRequest } from '@/types/api';

const PER_PAGE = 15;

export function useUsersPage() {
  const { user: currentUser } = useAuth();
  const activeRole = useActiveRole();
  const canWrite = activeRole === UserRole.Owner || activeRole === UserRole.Admin || activeRole === UserRole.Manager;
  const { getUsersPage, setUsersPage, invalidate } = useDataCache();
  const { activeOrgId } = useOrg();

  const [searchParams, setSearchParams] = useSearchParams();
  const roleFilter = searchParams.get('role') || '';
  const statusFilter = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || '1');
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');
  const debouncedSearch = useDebounce(searchInput, 300);

  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [resetTargetUser, setResetTargetUser] = useState<User | null>(null);

  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [removingFromOrgUser, setRemovingFromOrgUser] = useState<User | null>(null);
  const [isRemovingFromOrg, setIsRemovingFromOrg] = useState(false);

  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [pendingInvites, setPendingInvites] = useState<Invite[]>([]);
  const [revokingInvite, setRevokingInvite] = useState<Invite | null>(null);

  const cacheKey = makePaginatedCacheKey({ org: activeOrgId, search: debouncedSearch, role: roleFilter, status: statusFilter, page, perPage: PER_PAGE });
  const cached = getUsersPage(cacheKey);

  // Sync debounced search to URL, reset page
  useEffect(() => {
    if (debouncedSearch === (searchParams.get('q') || '')) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (debouncedSearch) next.set('q', debouncedSearch);
      else next.delete('q');
      next.delete('page');
      return next;
    }, { replace: true });
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (getUsersPage(cacheKey)) return;
    loadUsers();
  }, [cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadUsers() {
    try {
      const response = await getUsers({
        search: debouncedSearch || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        page,
        perPage: PER_PAGE,
      });
      setUsersPage(cacheKey, response);

      // Only fetch invites if user has write permissions
      if (canWrite) {
        try {
          const invitesData = await getOrgInvites('');
          const now = new Date();
          setPendingInvites(invitesData.filter(i => !i.isAccepted && new Date(i.expiresAt) > now));
        } catch {
          setPendingInvites([]);
        }
      } else {
        setPendingInvites([]);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  }

  function handlePageChange(newPage: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (newPage > 1) next.set('page', String(newPage));
      else next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleClearFilters() {
    setSearchInput('');
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('role');
      next.delete('status');
      next.delete('q');
      next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleRoleChange(role: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (role) next.set('role', role); else next.delete('role');
      next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleStatusChange(status: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (status) next.set('status', status); else next.delete('status');
      next.delete('page');
      return next;
    }, { replace: true });
  }

  function handleEditUser(user: User) {
    setEditingUser(user);
    setIsUserModalOpen(true);
  }

  function handleResetPassword(user: User) {
    setResetTargetUser(user);
    setIsResetModalOpen(true);
  }

  async function handleSaveUser(data: UpdateUserRequest) {
    if (!editingUser) return;
    setIsUserModalOpen(false);
    try {
      await updateUser(editingUser.id, data);
      invalidate(['users']);
      await loadUsers();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  }

  async function handleResetPasswordConfirm(data: ResetPasswordRequest) {
    if (!resetTargetUser) return;
    await resetUserPassword(resetTargetUser.id, data);
    setIsResetModalOpen(false);
    setResetTargetUser(null);
  }

  async function handleDeleteConfirm() {
    if (!deletingUser) return;
    const userToDelete = deletingUser;
    setIsDeleting(true);
    setDeletingUser(null);
    try {
      await deleteUser(userToDelete.id);
      // Optimistically remove from cache to avoid flash, then background-refresh
      if (cached) {
        setUsersPage(cacheKey, {
          ...cached,
          items: cached.items.filter(u => u.id !== userToDelete.id),
          totalCount: cached.totalCount - 1,
        });
      }
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
    } finally {
      setIsDeleting(false);
    }
  }

  async function handleAddUserSubmit(email: string, role: UserRole) {
    await addOrgMember(activeOrgId, { email, role });
    setIsAddUserModalOpen(false);
    invalidate(['users']);
    await loadUsers();
  }

  async function handleRevokeInviteConfirm() {
    if (!revokingInvite) return;
    const invite = revokingInvite;
    setRevokingInvite(null);
    try {
      await revokeInvite(invite.id);
      setPendingInvites(prev => prev.filter(i => i.id !== invite.id));
      loadUsers();
    } catch (error) {
      console.error('Failed to revoke invite:', error);
    }
  }

  async function handleRemoveFromOrgConfirm() {
    if (!removingFromOrgUser) return;
    const userToRemove = removingFromOrgUser;
    setIsRemovingFromOrg(true);
    setRemovingFromOrgUser(null);
    try {
      await removeOrgMember(activeOrgId, userToRemove.id);
      // Optimistically remove from list
      if (cached) {
        setUsersPage(cacheKey, {
          ...cached,
          items: cached.items.filter(u => u.id !== userToRemove.id),
          totalCount: cached.totalCount - 1,
        });
      }
      loadUsers();
    } catch (error) {
      console.error('Failed to remove user from organization:', error);
    } finally {
      setIsRemovingFromOrg(false);
    }
  }

  const users = cached?.items ?? [];
  const totalCount = cached?.totalCount ?? 0;
  const totalPages = cached?.totalPages ?? 1;
  const loading = !cached;

  return {
    users,
    pendingInvites,
    loading,
    totalCount,
    totalPages,
    page,
    currentUser,
    roleFilter,
    statusFilter,
    searchInput,
    setSearchInput,
    handleClearFilters,
    handleRoleChange,
    handleStatusChange,
    handlePageChange,
    isUserModalOpen,
    setIsUserModalOpen,
    editingUser,
    handleEditUser,
    handleSaveUser,
    isResetModalOpen,
    setIsResetModalOpen,
    resetTargetUser,
    handleResetPassword,
    handleResetPasswordConfirm,
    deletingUser,
    setDeletingUser,
    isDeleting,
    handleDeleteConfirm,
    removingFromOrgUser,
    setRemovingFromOrgUser,
    isRemovingFromOrg,
    handleRemoveFromOrgConfirm,
    isAddUserModalOpen,
    setIsAddUserModalOpen,
    handleAddUserSubmit,
    revokingInvite,
    setRevokingInvite,
    handleRevokeInviteConfirm,
  };
}

export type UsersPageState = ReturnType<typeof useUsersPage>;
